import { Controller } from "@hotwired/stimulus"

// Development-only placement tool for the landing page's flying bills
// (home/index ?edit=true). Click a bill to select it, drag to move, use the
// Rotate slider (or scroll over the bill, Shift for fine steps), and Replace
// to cycle through the other bill artworks. Arrow keys nudge, [ ] rotate.
// "Copy" puts every bill's art and offsets on the clipboard to bake in.
//
// It only writes the --bill-x / --bill-y / --bill-r custom properties the
// stylesheet already reads, plus the img src, so what you see while editing
// is exactly what the baked values render. Bills on a device rail
// (.landing-bill-rail) are placed in cqw, fractions of the device's width, so
// they stay stuck to it at every size; the deck's bill is in px.
export default class extends Controller {
  static targets = ["output", "selected", "rotate", "rotateLabel"]
  static values = { variants: Object }

  connect() {
    this.bills = [...document.querySelectorAll("[data-bill]")]
    this.state = Object.fromEntries(this.bills.map((bill) => [bill.dataset.bill, this.read(bill)]))
    this.bills.forEach((bill) => this.arm(bill))
    this.keydown = this.onKey.bind(this)
    window.addEventListener("keydown", this.keydown)
    this.select(this.bills[0])
  }

  disconnect() {
    window.removeEventListener("keydown", this.keydown)
  }

  read(bill) {
    const style = getComputedStyle(bill)
    return {
      unit: this.unitFor(bill),
      art: this.artFor(bill.getAttribute("src")),
      x: parseFloat(style.getPropertyValue("--bill-x")) || 0,
      y: parseFloat(style.getPropertyValue("--bill-y")) || 0,
      r: parseFloat(style.getPropertyValue("--bill-r")) || 0
    }
  }

  unitFor(bill) {
    return bill.closest(".landing-bill-rail, .landing-bill-host") ? "cqw" : "px"
  }

  // Screen px per unit: a rail bill's cqw is 1% of the rail's width.
  pxPerUnit(bill) {
    const rail = bill.closest(".landing-bill-rail, .landing-bill-host")
    if (rail) return rail.getBoundingClientRect().width / 100
    return bill.currentCSSZoom || 1
  }

  // Fingerprinted asset URL -> its variant name (e.g. "bill-paper-4").
  artFor(src) {
    return Object.keys(this.variantsValue).find((name) => src.includes(name)) || src
  }

  arm(bill) {
    bill.style.pointerEvents = "auto"
    bill.style.cursor = "grab"
    bill.closest(".landing-bill-rail")?.style.setProperty("z-index", "20")
    bill.draggable = false
    bill.loading = "eager"

    bill.addEventListener("pointerdown", (event) => this.dragStart(event, bill))
    bill.addEventListener("wheel", (event) => this.wheel(event, bill), { passive: false })
  }

  select(bill) {
    if (!bill) return
    this.active = bill.dataset.bill
    this.bills.forEach((b) => {
      b.style.outline = b === bill ? "3px solid #3D8AFE" : "2px dashed rgba(61, 138, 254, .5)"
      b.style.outlineOffset = "4px"
    })
    this.render()
  }

  get activeBill() {
    return this.bills.find((b) => b.dataset.bill === this.active)
  }

  dragStart(event, bill) {
    event.preventDefault()
    this.select(bill)
    const scale = this.pxPerUnit(bill)
    const round = (n) => Math.round(n * 10) / 10
    const start = { px: event.clientX, py: event.clientY, ...this.state[this.active] }
    bill.setPointerCapture(event.pointerId)
    bill.style.cursor = "grabbing"

    const move = (e) => {
      this.update(bill, {
        x: round(start.x + (e.clientX - start.px) / scale),
        y: round(start.y + (e.clientY - start.py) / scale)
      })
    }
    const end = () => {
      bill.style.cursor = "grab"
      bill.removeEventListener("pointermove", move)
      bill.removeEventListener("pointerup", end)
      bill.removeEventListener("pointercancel", end)
    }
    bill.addEventListener("pointermove", move)
    bill.addEventListener("pointerup", end)
    bill.addEventListener("pointercancel", end)
  }

  wheel(event, bill) {
    event.preventDefault()
    this.select(bill)
    const step = event.shiftKey ? 0.5 : 2
    const r = this.state[this.active].r + (event.deltaY > 0 ? step : -step)
    this.update(bill, { r: Math.round(r * 10) / 10 })
  }

  // Panel slider.
  rotate() {
    this.update(this.activeBill, { r: Number(this.rotateTarget.value) })
  }

  replaceNext() { this.replace(1) }
  replacePrev() { this.replace(-1) }

  replace(direction) {
    const names = Object.keys(this.variantsValue)
    const current = names.indexOf(this.state[this.active].art)
    const art = names[(current + direction + names.length) % names.length]
    this.activeBill.src = this.variantsValue[art]
    this.update(this.activeBill, { art })
  }

  onKey(event) {
    if (!this.active || event.target.matches("input, textarea")) return
    const { x, y, r } = this.state[this.active]
    const step = event.shiftKey ? 10 : 1
    const moves = {
      ArrowLeft: { x: x - step },
      ArrowRight: { x: x + step },
      ArrowUp: { y: y - step },
      ArrowDown: { y: y + step },
      "[": { r: r - step },
      "]": { r: r + step }
    }
    if (!moves[event.key]) return
    event.preventDefault()
    this.update(this.activeBill, moves[event.key])
  }

  update(bill, change) {
    const name = bill.dataset.bill
    this.state = { ...this.state, [name]: { ...this.state[name], ...change } }
    const { unit, x, y, r } = this.state[name]
    const suffix = unit === "px" ? "px" : ""
    bill.style.setProperty("--bill-x", `${x}${suffix}`)
    bill.style.setProperty("--bill-y", `${y}${suffix}`)
    bill.style.setProperty("--bill-r", `${r}deg`)
    this.render()
  }

  text() {
    return Object.entries(this.state)
      .map(([name, { unit, art, x, y, r }]) => `${name}: ${art}, x ${x}${unit}, y ${y}${unit}, r ${r}deg`)
      .join("\n")
  }

  render() {
    const { art, r } = this.state[this.active]
    this.selectedTarget.textContent = `${this.active} · ${art}`
    this.rotateTarget.value = r
    this.rotateLabelTarget.textContent = `${r}°`
    this.outputTarget.value = this.text()
  }

  async copy(event) {
    this.outputTarget.select()
    try {
      await navigator.clipboard.writeText(this.text())
      this.flash(event.currentTarget, "Copied")
    } catch (error) {
      console.error("[bill-editor] clipboard write failed", error)
      this.flash(event.currentTarget, "Select the text and copy it")
    }
  }

  flash(button, label) {
    const original = button.textContent
    button.textContent = label
    setTimeout(() => { button.textContent = original }, 1200)
  }
}
